import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { CartCharges, CartItem, CartItemType, CartSectionType, CartSelectedCustomization } from '../../../../../../models/order/workflow/order-cart.model';
import { CartItemDisclosure, Choice, Customization } from '../../../../../../models/order/api-models/order-pd-response.model';
import { OrderFlowType, ProductType } from '../../../../../../models/order/order.model';
import { OrderCustomizationsStateService } from '../order-customization/order-customization.state.service';
import { OrderStateServiceV2 } from '../../order-state.service';
import { VideoPackageSelectionStateService } from '../video/video-package-selection/video-package-selection-state.service';
import { VideoProductResponseService } from '../../order-api-response/video-pd-response.service';
import { VideoCartData } from '../../../../../../models/order/workflow/video/video-cart.model';
import { VideoCommercialSatelliteCartStateService } from './order-video-commercial-satellite-cart.state.service';
import { StreamHardwareSelectionStateService } from '../video/stream-hardware-selection/stream-hardware-selection.state.service';
import { SatelliteHardwareSelectionData, StreamHardwareSelectionData } from '../../../../../../models/order/workflow/video/video-package-selection.model';
import { SatelliteHardwareSelectionStateService } from '../video/satellite-hardware-selection/satellite-hardware-selection.state.service';


@Injectable({ providedIn: 'root' })
export class VideoCartStateService {
    private orderStateService = inject(OrderStateServiceV2);
    private videoPackageStateService = inject(VideoPackageSelectionStateService);
    private videoPdResponseService = inject(VideoProductResponseService);
    private orderCustomizationsStateService = inject(OrderCustomizationsStateService);
    private videoCommercialSatelliteCartStateService = inject(VideoCommercialSatelliteCartStateService);
    private streamHardwareSelectionStateService = inject(StreamHardwareSelectionStateService);
    private satelliteHardwareSelectionStateService = inject(SatelliteHardwareSelectionStateService);
    private selectedCustomizations: CartSelectedCustomization[] = [];
    private readonly residentialSatelliteMainHardwareChoiceCodes = new Set([
        'DTV-EQUIPMENT-MAIN-GENIE',
        'DTV-EQUIPMENT-MAIN-GENIE2'
    ]);
    private readonly residentialSatelliteSubHardwareChoiceCodes = new Set([
        'DTV-EQUIPMENT-GENIE-ADDTL-MINI',
        'DTV-EQUIPMENT-GENIE-ADDTL-HD',
        'DTV-EQUIPMENT-GENIE2-ADDTL-MINI-GEMINI',
        'DTV-EQUIPMENT-GENIE2-ADDTL-MINI-WRLS'
    ]);
    private readonly genieMiniChoiceCode = 'DTV-EQUIPMENT-GENIE-ADDTL-MINI';
    private readonly hdReceiverChoiceCode = 'DTV-EQUIPMENT-GENIE-ADDTL-HD';
    private readonly genieMiniCheckboxKey = 'GENIE-CHECKBOX-DTV-EQUIPMENT-GENIE-ADDTL-MINI';
    private readonly genieMiniQuantityKey = 'GENIE-QTY-DTV-EQUIPMENT-GENIE-ADDTL-MINI';
    private readonly hdReceiverCheckboxKey = 'GENIE-CHECKBOX-DTV-EQUIPMENT-GENIE-ADDTL-HD';
    private readonly hdReceiverQuantityKey = 'GENIE-QTY-DTV-EQUIPMENT-GENIE-ADDTL-HD';
    private readonly wirelessGeminiChoiceCode = 'DTV-EQUIPMENT-GENIE2-ADDTL-MINI-GEMINI';
    private readonly wirelessGenieMiniChoiceCode = 'DTV-EQUIPMENT-GENIE2-ADDTL-MINI-WRLS';
    private readonly tvAccessFeeChoiceCode = 'DTV-TV-ACCESS-FEE';
    private readonly includedOptionalOffersParentCode = 'DTV-OPTIONAL-OFFERS';
    private readonly videoAdvanceBillPaymentProductTypes = new Set(['video', 'dtv', 'atv']);
    private readonly GEMINI_HARDWARE_FEE = 49;
    private readonly WIRELESS_GEMINI_HARDWARE_FEE = 99;

    private isIncludedWithPackage(choice: Choice | undefined): boolean {
        return choice?.IncludedWithPackage === 'true' || choice?.IncludedWithPackage === true;
    }

    private isOptionalOffersChild(offer: CartSelectedCustomization): boolean {
        const path = offer.customizationReference?.path || [];
        return path.some(node => {
            const code = node.type === 'choice'
                ? (node.customization as Choice).ChoiceCode
                : (node.customization as Customization).CustomizationCode;
            return code === this.includedOptionalOffersParentCode;
        });
    }

    private shouldShowIncludedLabel(offer: CartSelectedCustomization): boolean {
        return this.isIncludedWithPackage(offer.Choice) &&
            (offer.Choice?.Fee?.Fee || 0) === 0 &&
            this.isOptionalOffersChild(offer);
    }

    private getResidentialSatelliteHardwareLabel(choiceCode: string, choiceName: string, satelliteHardwareSelection?: SatelliteHardwareSelectionData | null): string {
        if (choiceCode === 'DTV-EQUIPMENT-MAIN-GENIE2') {
            const genie2HardwareLabel = this.getSatelliteGenie2HardwareLabel(satelliteHardwareSelection);
            return genie2HardwareLabel ? `Genie 2 - ${genie2HardwareLabel}` : 'Genie 2';
        }

        if (choiceCode === 'DTV-EQUIPMENT-MAIN-GENIE') {
            return 'Genie';
        }

        if (choiceCode.startsWith('DTV-EQUIPMENT-GENIE2-ADDTL')) {
            const tvQty = this.getSatelliteTvQuantity(satelliteHardwareSelection);
            if (!this.isCommercial() && tvQty > 0) {
                if (choiceCode === this.wirelessGeminiChoiceCode) {
                    return `Genie 2 - ${this.getWirelessGeminiLabel(tvQty)}`;
                }

                if (choiceCode === this.wirelessGenieMiniChoiceCode && choiceName) {
                    return `Genie 2 - ${tvQty} ${choiceName}`;
                }
            }

            return choiceName ? `Genie 2 - ${choiceName}` : 'Genie 2';
        }

        if (choiceCode.startsWith('DTV-EQUIPMENT-GENIE-ADDTL')) {
            const subHardwareQty = this.getSatelliteGenieSubHardwareQuantity(choiceCode, satelliteHardwareSelection);
            const quantityLabel = subHardwareQty > 0 ? `(${subHardwareQty})` : '';
            return choiceName ? `Genie - ${choiceName}${quantityLabel ? ' - ' + quantityLabel : ''}` : 'Genie';
        }

        return choiceName;
    }

    private getSatelliteGenie2HardwareLabel(satelliteHardwareSelection?: SatelliteHardwareSelectionData | null): string {
        const genie2ChoiceCode = satelliteHardwareSelection?.genie2Equipment;
        const tvQty = this.getSatelliteTvQuantity(satelliteHardwareSelection);

        if (!genie2ChoiceCode || tvQty <= 0) {
            return '';
        }

        if (genie2ChoiceCode === this.wirelessGeminiChoiceCode) {
            return this.getWirelessGeminiLabel(tvQty);
        }

        const choiceName = this.videoPdResponseService.findChoiceCode(genie2ChoiceCode)?.Name || '';
        return choiceName ? `${tvQty} ${choiceName}` : '';
    }

    private getSatelliteGenieSubHardwareQuantity(choiceCode: string, satelliteHardwareSelection?: SatelliteHardwareSelectionData | null): number {
        const genieOptions = satelliteHardwareSelection?.genieOptions || [];

        if (choiceCode === this.genieMiniChoiceCode) {
            const genieMini = genieOptions.find(option => option[this.genieMiniCheckboxKey] === true);
            return this.getNumericValue(this.getSatelliteGenieQuantityValue(genieMini?.[this.genieMiniQuantityKey]));
        }

        if (choiceCode === this.hdReceiverChoiceCode) {
            const hdReceiver = genieOptions.find(option => option[this.hdReceiverCheckboxKey] === true);
            return this.getNumericValue(this.getSatelliteGenieQuantityValue(hdReceiver?.[this.hdReceiverQuantityKey]));
        }

        return 0;
    }

    private getSatelliteGenieQuantityValue(value: boolean | number | string | null | undefined): number | string | null | undefined {
        return typeof value === 'boolean' ? null : value;
    }

    private getSatelliteTvQuantity(satelliteHardwareSelection?: SatelliteHardwareSelectionData | null): number {
        const tvQty = satelliteHardwareSelection?.tvQty;

        if (typeof tvQty === 'number') {
            return tvQty;
        }

        if (typeof tvQty === 'string') {
            return this.getNumericValue(tvQty);
        }

        if (tvQty?.quantity) {
            return Number(tvQty.quantity);
        }

        return 0;
    }

    private getWirelessGeminiLabel(quantity: number): string {
        return `${quantity} Wireless Gemini${quantity === 1 ? '' : 's'}`;
    }

    private isResidentialSatelliteSubHardware(offer: CartSelectedCustomization): boolean {
        const choiceCode = offer.Choice?.ChoiceCode || '';
        return !this.isCommercial() && this.residentialSatelliteSubHardwareChoiceCodes.has(choiceCode);
    }

    private isResidentialSatelliteGenie2SubHardware(offer: CartSelectedCustomization): boolean {
        const choiceCode = offer.Choice?.ChoiceCode || '';
        return !this.isCommercial() && (
            choiceCode === this.wirelessGeminiChoiceCode ||
            choiceCode === this.wirelessGenieMiniChoiceCode
        );
    }

    private getNumericValue(value: number | string | null | undefined): number {
        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : 0;
        }

        if (typeof value === 'string') {
            const parsedValue = Number(value.replace(/[$,]/g, ''));
            return Number.isFinite(parsedValue) ? parsedValue : 0;
        }

        return 0;
    }

    private isVideoAdvanceBillPaymentChoice(choice?: object | null, customization?: object | null): boolean {
        const choiceProductType = this.getProductType(choice);
        const customizationProductType = this.getProductType(customization);
        const productType = (choiceProductType || customizationProductType).toLocaleLowerCase();
        return productType ? this.videoAdvanceBillPaymentProductTypes.has(productType) : false;
    }

    private getProductType(value?: object | null): string {
        if (!value || !('ProductType' in value)) {
            return '';
        }

        const productType = value.ProductType;
        return typeof productType === 'string' ? productType : '';
    }

    private getCartDisclosureList(choice?: Choice | null): CartItemDisclosure[] {
        const parsedDisclosures = this.parseCartDisclosureArray(choice?.CartDisclosureArray);
        if (parsedDisclosures.length) {
            return parsedDisclosures;
        }

        if (choice?.CartDisclosureList?.length) {
            return choice.CartDisclosureList;
        }

        return [];
    }

    private parseCartDisclosureArray(cartDisclosureArray?: string[] | string | null): CartItemDisclosure[] {
        if (!cartDisclosureArray) {
            return [];
        }

        const entries = (Array.isArray(cartDisclosureArray) ? cartDisclosureArray : [cartDisclosureArray])
            .flatMap(disclosure => typeof disclosure === 'string' ? disclosure.split('|') : []);

        return entries.reduce<CartItemDisclosure[]>((disclosures, entry) => {
            const [label, fee] = entry.split('~').map(value => value?.trim());
            const normalizedFee = fee?.replace(/[$,]/g, '');

            if (!label || !normalizedFee || !Number.isFinite(Number(normalizedFee))) {
                return disclosures;
            }

            disclosures.push({
                Label: label,
                Fee: normalizedFee
            });

            return disclosures;
        }, []);
    }

    private getFeePerUnit(offer: CartSelectedCustomization): number {
        const choice = offer.Choice as any;
        const fee = offer.Choice?.Fee as any;
        const customization = offer.Customization as any;

        return this.getNumericValue(
            fee?.feePerUnit ??
            fee?.FeePerUnit ??
            fee?.feeperunit ??
            choice?.feePerUnit ??
            choice?.FeePerUnit ??
            choice?.feeperunit ??
            customization?.feePerUnit ??
            customization?.FeePerUnit ??
            customization?.feeperunit
        );
    }

    private isResidentialSatelliteTvAccessFeeSource(offer: CartSelectedCustomization): boolean {
        const choiceCode = offer.Choice?.ChoiceCode || '';
        const customizationCode = offer.Customization?.CustomizationCode || '';
        const sourceText = `${offer.Choice?.CartName || ''} ${offer.Choice?.Name || ''} ${offer.Customization?.Name || ''}`.toLocaleLowerCase();

        return !this.isCommercial() &&
            this.getFeePerUnit(offer) > 0 &&
            (
                choiceCode.startsWith('DTV-EQUIPMENT') ||
                customizationCode.startsWith('DTV-EQUIPMENT') ||
                choiceCode === 'DTV-RECEIVER-COUNT-NUMBER' ||
                choiceCode.toLocaleLowerCase().includes('access') ||
                customizationCode.toLocaleLowerCase().includes('access') ||
                sourceText.includes('tv access') ||
                sourceText.includes('access fee')
            );
    }

    private isDedicatedTvAccessFeeOffer(offer: CartSelectedCustomization): boolean {
        const choiceCode = offer.Choice?.ChoiceCode || '';
        const customizationCode = offer.Customization?.CustomizationCode || '';
        const sourceText = `${offer.Choice?.CartName || ''} ${offer.Choice?.Name || ''} ${offer.Customization?.Name || ''}`.toLocaleLowerCase();

        return !choiceCode.startsWith('DTV-EQUIPMENT') &&
            !customizationCode.startsWith('DTV-EQUIPMENT') &&
            choiceCode !== 'DTV-RECEIVER-COUNT-NUMBER' &&
            (
                choiceCode.toLocaleLowerCase().includes('access') ||
                customizationCode.toLocaleLowerCase().includes('access') ||
                sourceText.includes('tv access') ||
                sourceText.includes('access fee')
            );
    }

    private getSatelliteTvAccessFeeOffer(): CartSelectedCustomization | null {
        const selectedOffer = this.selectedCustomizations.find(offer => this.isResidentialSatelliteTvAccessFeeSource(offer));
        if (selectedOffer) {
            return selectedOffer;
        }

        const originalPdOffer = this.getOriginalDtvPdAccessFeeOffer();
        if (originalPdOffer) {
            return originalPdOffer;
        }

        const pdFeeCustomization = this.videoPdResponseService.getCustomizations()
            .find(customization => customization.customization.Choices?.Choice?.some(choice => {
                const offer = { Choice: choice, Customization: customization.customization };
                return this.isResidentialSatelliteTvAccessFeeSource(offer);
            }));
        const pdFeeChoice = pdFeeCustomization?.customization.Choices?.Choice?.find(choice => {
            const offer = { Choice: choice, Customization: pdFeeCustomization.customization };
            return this.isResidentialSatelliteTvAccessFeeSource(offer);
        });

        return pdFeeChoice && pdFeeCustomization
            ? { Choice: pdFeeChoice, Customization: pdFeeCustomization.customization }
            : null;
    }

    private getOriginalDtvPdAccessFeeOffer(): CartSelectedCustomization | null {
        const dtvComponent = this.videoPdResponseService.getDTVProductComponent();
        const customizations = dtvComponent?.Customizations?.Customization || [];
        return this.findAccessFeeOfferInCustomizations(customizations);
    }

    private findAccessFeeOfferInCustomizations(customizations: any[]): CartSelectedCustomization | null {
        for (const customization of customizations || []) {
            const choices = customization?.Choices?.Choice || [];

            for (const choice of choices) {
                const offer = { Choice: choice, Customization: customization };
                if (this.isResidentialSatelliteTvAccessFeeSource(offer)) {
                    return offer;
                }

                const nestedOffer = this.findAccessFeeOfferInCustomizations(choice?.Customizations?.Customization || []);
                if (nestedOffer) {
                    return nestedOffer;
                }
            }
        }

        return null;
    }

    private createTvAccessFeeCartItem(offer: CartSelectedCustomization, satelliteHardwareSelection: SatelliteHardwareSelectionData | null): CartItem | null {
        const tvQty = this.getSatelliteTvQuantity(satelliteHardwareSelection);
        const feePerUnit = this.getFeePerUnit(offer);

        if (tvQty <= 0 || feePerUnit <= 0) {
            return null;
        }

        return {
            disclosure: offer.Choice?.CartDisclosure || '',
            amount: feePerUnit * tvQty,
            name: 'TV Access Fee',
            choiceCode: this.tvAccessFeeChoiceCode,
            choice: offer.Choice,
            customization: offer.Customization,
            metadata: offer,
            cartSectionType: CartSectionType.Monthly,
            type: CartItemType.Fee,
            productType: ProductType.Video,
            CartDisclosureList: this.getCartDisclosureList(offer.Choice),
            excludeFromProductSummary: offer.Choice?.ExcludeFromProductSummary?.toLocaleLowerCase() === 'true'
        };
    }

    private getVideoCartItemLabel(
        offer: CartSelectedCustomization,
        streamHardwareSelection: StreamHardwareSelectionData | null,
        satelliteHardwareSelection?: SatelliteHardwareSelectionData | null
    ): string {
        const choiceCode = offer.Choice?.ChoiceCode || '';
        const choiceName = offer.Choice?.Name || '';
        const hardwareOptionName = streamHardwareSelection?.hardwareOptionName || '';

        const fallbackLabel = this.getResidentialSatelliteHardwareLabel(
            choiceCode,
            choiceName,
            satelliteHardwareSelection
        );

        if (this.isCommercial() || !streamHardwareSelection) {
            return fallbackLabel;
        }

        const isStreamEquipment = offer.Customization?.CustomizationCode === 'ATV-EQUIPMENT-PAY';
        const isSelectedStreamHardware =
            streamHardwareSelection.hardwareOption === choiceCode ||
            choiceName.toLocaleLowerCase().includes('wireless gemini') ||
            hardwareOptionName.toLocaleLowerCase().includes('wireless gemini');

        const selectedHardwareCount = Number(streamHardwareSelection.numberofHardware) || 0;

        const isStreamHardwareMatch = (isStreamEquipment || isSelectedStreamHardware) && selectedHardwareCount > 0;

        return isStreamHardwareMatch ? (hardwareOptionName || fallbackLabel) : fallbackLabel;
    }

    private _cart = signal<VideoCartData | null>(null);
    private orderCustomizations = this.orderCustomizationsStateService.selectedCustomizations;

    cart = this._cart.asReadonly();

    orderType = computed(() => {
        // return 'smb'
        return (this.orderStateService.order()?.orderSetup?.orderOptions?.orderType ?? '')
    });

    isCommercial = computed(() => {
        return this.orderType() === 'smb'
    })

    constructor() {
        effect(() => {
            const order = this.orderCustomizations();
            const pdResponse = this.videoPdResponseService.updateOfferResponse();
            const streamHardwareSelection = this.streamHardwareSelectionStateService.state();
            const satelliteHardwareSelection = this.satelliteHardwareSelectionStateService.state();
            if (pdResponse && pdResponse.Components.Component.ProductType == "DTV" && this.isCommercial()) {
                this._cart.set(this.videoCommercialSatelliteCartStateService.cart());
                return;
            }
            this._cart.set(null);
            this.selectedCustomizations = [];
            if (order && this.videoPackageStateService.isPlanSelected()) {
                this.setupSelectedCustomizations();
                this.setupCartFromOrder(streamHardwareSelection, satelliteHardwareSelection);
            }
        });
    }

    private setupCartFromOrder(streamHardwareSelection: StreamHardwareSelectionData | null, satelliteHardwareSelection: SatelliteHardwareSelectionData | null) {
        const videoCharges = this.setupVideoCharges(streamHardwareSelection, satelliteHardwareSelection);
        if (videoCharges) {
            const firstBillCharges = this.setupFirstBillCharges(streamHardwareSelection, satelliteHardwareSelection);
            const todaysCharges = this.setupTodaysCharges(streamHardwareSelection, satelliteHardwareSelection);
            const packagesCharges = this.setupPackageCharges(streamHardwareSelection, satelliteHardwareSelection);
            const savingAndPromotionCharges = this.setupSavingAndPromotionCharges(streamHardwareSelection, satelliteHardwareSelection);
            videoCharges.firstBillCharges = firstBillCharges;
            videoCharges.todaysCharges = todaysCharges;
            videoCharges.packagesCharges = packagesCharges;
            videoCharges.savingAndPromotionCharges = savingAndPromotionCharges;
        }

        this._cart.set(videoCharges);
    }

    private setupSelectedCustomizations() {
        if (!this.orderCustomizations().length) {
            this.selectedCustomizations = [];
            return;
        }
        const orderVideoCustomizations = this.orderCustomizations().filter(c => c.type === OrderFlowType.Video);
        if (orderVideoCustomizations.length) {
            const selectedCustomizations: CartSelectedCustomization[] = [];
            const seenChoiceCodes = new Set<string>();
            for (const customization of orderVideoCustomizations) {
                const choiceCode = customization.choice?.ChoiceCode;
                if (choiceCode && seenChoiceCodes.has(choiceCode)) continue;
                if (choiceCode) seenChoiceCodes.add(choiceCode);
                selectedCustomizations.push({ Customization: customization.customization, Choice: customization.choice, customizationReference: customization.customizationReference });
            }

            selectedCustomizations.sort((a, b) => (a.Choice?.CartSeq || 0) - (b.Choice?.CartSeq || 0));
            this.selectedCustomizations = selectedCustomizations;
        }
    }

    private setupFirstBillCharges(streamHardwareSelection: StreamHardwareSelectionData | null, satelliteHardwareSelection: SatelliteHardwareSelectionData | null): CartCharges | undefined {
        const firstBillCharges: CartCharges = {
            totalAmount: 0,
            items: []
        };

        if (this.selectedCustomizations.length > 0) {
            for (const offer of this.selectedCustomizations) {
                if (this.isResidentialSatelliteGenie2SubHardware(offer)) {
                    continue;
                }

                if (offer.Customization.CartCategory?.toLocaleLowerCase() == 'firstbill' || offer.Choice.CartCategory?.toLocaleLowerCase() == 'firstbill') {
                    const choiceCode = offer.Choice?.ChoiceCode || '';
                    const cartItem: CartItem = {
                        disclosure: offer.Choice?.CartDisclosure || '',
                        amount: offer.Choice?.Fee.Fee || 0,
                        name: this.getVideoCartItemLabel(offer, streamHardwareSelection, satelliteHardwareSelection),
                        choiceCode: choiceCode,
                        choice: offer.Choice,
                        customization: offer.Customization,
                        metadata: offer,
                        productType: ProductType.Video
                    }
                    firstBillCharges.items.push(cartItem);
                    firstBillCharges.totalAmount += cartItem.amount;
                }
            }
        }
        return firstBillCharges;
    }

    private setupTodaysCharges(streamHardwareSelection: StreamHardwareSelectionData | null, satelliteHardwareSelection: SatelliteHardwareSelectionData | null): CartCharges | undefined {
        const todayCharges: CartCharges = {
            totalAmount: 0,
            items: []
        };


        if (this.selectedCustomizations.length > 0) {
            for (const offer of this.selectedCustomizations) {
                if (this.isResidentialSatelliteGenie2SubHardware(offer)) {
                    continue;
                }

                const feeTerm = offer.Choice?.Fee?.FeeTerm || '';
                if (offer.Customization.CartCategory == 'TodaysCharges' || offer.Choice.CartCategory == 'TodaysCharges' || feeTerm.toLocaleLowerCase() == 'one time') {
                    const choiceCode = offer.Choice?.ChoiceCode || '';
                    let fee = offer.Choice?.Fee?.Fee || 0;

                    if (satelliteHardwareSelection?.hardwareType) {
                        fee = this.dtvResidentialSatelliteSubHardwarePricing(offer) || fee;
                    }
                    const cartItem: CartItem = {
                        disclosure: offer.Choice?.CartDisclosure || '',
                        amount: fee,
                        name: this.getVideoCartItemLabel(offer, streamHardwareSelection, satelliteHardwareSelection),
                        choiceCode: choiceCode,
                        choice: offer.Choice,
                        customization: offer.Customization,
                        metadata: offer,
                        type: CartItemType.Fee,
                        cartSectionType: CartSectionType.TodaysCharges,
                        productType: ProductType.Video,
                        CartDisclosureList: this.getCartDisclosureList(offer.Choice)
                    }
                    todayCharges.items.push(cartItem);
                    todayCharges.totalAmount += cartItem.amount;
                }
            }
        }

        //Check for Advance Bill Payment and add to todays charges
        const abpState = this.orderStateService.order()?.advanceBillPayment;
        if (abpState && abpState.customization && this.isVideoAdvanceBillPaymentChoice(abpState.customization.Choices?.Choice, abpState.customization)) {
            const cartItem: CartItem = {
                disclosure: abpState.customization.Choices?.Choice?.CartDisclosure || '',
                amount: abpState.customization.Choices?.Choice?.Fee.Fee || 0,
                name: abpState.customization.Choices?.Choice?.Name || '',
                choiceCode: abpState.customization.Choices?.Choice?.ChoiceCode || '',
                choice: abpState.customization.Choices?.Choice,
                customization: abpState.customization as unknown as Customization,
                metadata: {} as CartSelectedCustomization,
                type: CartItemType.Fee,
                cartSectionType: CartSectionType.TodaysCharges,
                productType: ProductType.Video,
                CartDisclosureList: this.getCartDisclosureList(abpState.customization.Choices?.Choice)
            };
            todayCharges.items.push(cartItem);
            todayCharges.totalAmount += cartItem.amount;
        }

        const dhFee = this.videoPdResponseService.getDeliveryAndHandlingFee();
        if (dhFee) {
            const cartItem: CartItem = {
                disclosure: dhFee.disclosure,
                amount: dhFee.amount,
                name: dhFee.name,
                choiceCode: 'Delivery and Handling fee',
                choice: null as any,
                customization: null as any,
                metadata: {} as CartSelectedCustomization,
                type: CartItemType.Fee,
                cartSectionType: CartSectionType.TodaysCharges,
                productType: ProductType.Video,
            };
            todayCharges.items.push(cartItem);
            todayCharges.totalAmount += cartItem.amount;
        }

        return todayCharges;
    }

    private setupPackageCharges(streamHardwareSelection: StreamHardwareSelectionData | null, satelliteHardwareSelection: SatelliteHardwareSelectionData | null): CartCharges | undefined {
        const packageCharges: CartCharges = {
            totalAmount: 0,
            items: []
        };

        if (this.selectedCustomizations.length > 0) {
            for (const offer of this.selectedCustomizations) {
                if (this.isResidentialSatelliteGenie2SubHardware(offer)) {
                    continue;
                }

                if (offer.Choice.CartCategory == 'Package') {
                    const choiceCode = offer.Choice?.ChoiceCode || '';
                    const cartItem: CartItem = {
                        disclosure: offer.Choice?.CartDisclosure || '',
                        amount: offer.Choice?.Fee.Fee || 0,
                        name: this.getVideoCartItemLabel(offer, streamHardwareSelection, satelliteHardwareSelection),
                        choiceCode: choiceCode,
                        choice: offer.Choice,
                        customization: offer.Customization,
                        metadata: offer,
                        type: CartItemType.Fee,
                        cartSectionType: CartSectionType.Packages,
                        productType: ProductType.Video
                    }
                    packageCharges.items.push(cartItem);
                    packageCharges.totalAmount += cartItem.amount;
                }
            }
        }

        return packageCharges;
    }

    private setupSavingAndPromotionCharges(streamHardwareSelection: StreamHardwareSelectionData | null, satelliteHardwareSelection: SatelliteHardwareSelectionData | null): CartCharges | undefined {
        const savingAndPromotionCharges: CartCharges = {
            totalAmount: 0,
            items: []
        };

        if (this.selectedCustomizations.length > 0) {
            for (const offer of this.selectedCustomizations) {
                if (this.isResidentialSatelliteGenie2SubHardware(offer)) {
                    continue;
                }

                if (offer.Choice.CartCategory == 'SavingAndPromotions') {
                    const choiceCode = offer.Choice?.ChoiceCode || '';
                    const cartItem: CartItem = {
                        disclosure: offer.Choice?.CartDisclosure || '',
                        amount: offer.Choice?.Fee.Fee || 0,
                        name: this.getVideoCartItemLabel(offer, streamHardwareSelection, satelliteHardwareSelection),
                        choiceCode: choiceCode,
                        choice: offer.Choice,
                        customization: offer.Customization,
                        metadata: offer,
                        type: CartItemType.Promotion, //Make it promotion if it is in saving and promotion category
                        cartSectionType: CartSectionType.SavingsAndPromotions,
                        productType: ProductType.Video
                    }
                    savingAndPromotionCharges.items.push(cartItem);
                    savingAndPromotionCharges.totalAmount += cartItem.amount;
                }
            }
        }

        return savingAndPromotionCharges;
    }

    private setupVideoCharges(streamHardwareSelection: StreamHardwareSelectionData | null, satelliteHardwareSelection: SatelliteHardwareSelectionData | null): VideoCartData | undefined {
        const videoComponent = this.videoPdResponseService.getSelectedComponent();
        const videoCartData: VideoCartData = {
            name: videoComponent?.ComponentName || '',
            price: videoComponent?.Price || 0,
            promoPrice: videoComponent?.PromoPrice || 0,
            monthly: {
                totalAmount: videoComponent?.Price || 0,
                items: [],
                promotions: {
                    totalAmount: 0,
                    items: []
                }
            }
        };

        for (const offer of this.selectedCustomizations) {
            if (this.isResidentialSatelliteGenie2SubHardware(offer)) {
                continue;
            }

            const showIncludedLabel = this.shouldShowIncludedLabel(offer);
            const isMonthlyVideoCharge = offer.Choice?.Fee?.FeeTerm === 'Monthly' && (offer.Choice.CartCategory == 'Video' || (offer.Choice.CartCategory == null && offer.Customization.CartCategory == 'Video'));
            const isRawDedicatedTvAccessFeeOffer = this.isResidentialSatelliteTvAccessFeeSource(offer) && this.isDedicatedTvAccessFeeOffer(offer);

            if (!isRawDedicatedTvAccessFeeOffer && (isMonthlyVideoCharge || showIncludedLabel)) {
                const cartItem: CartItem = {
                    disclosure: offer.Choice?.CartDisclosure || '',
                    amount: offer.Choice?.Fee.Fee || 0,
                    name: this.getVideoCartItemLabel(offer, streamHardwareSelection, satelliteHardwareSelection),
                    choiceCode: offer.Choice?.ChoiceCode || '',
                    choice: offer.Choice,
                    customization: offer.Customization,
                    metadata: offer,
                    cartSectionType: CartSectionType.Monthly,
                    type: CartItemType.Fee,
                    productType: ProductType.Video,
                    excludeFromProductSummary: offer.Choice?.ExcludeFromProductSummary?.toLocaleLowerCase() === 'true',
                    CartDisclosureList: this.getCartDisclosureList(offer.Choice),
                    showIncludedLabel
                }

                if (cartItem.amount < 0) {
                    cartItem.type = CartItemType.Promotion; //Make it promotion if amount is negative
                    videoCartData.monthly.promotions.items.push(cartItem);
                    videoCartData.monthly.promotions.totalAmount += cartItem.amount;
                } else {
                    cartItem.type = CartItemType.Fee;
                    videoCartData.monthly.items.push(cartItem);
                    videoCartData.monthly.totalAmount += cartItem.amount;
                }

            }
        }

        const tvAccessFeeOffer = this.getSatelliteTvAccessFeeOffer();
        const tvAccessFeeCartItem = tvAccessFeeOffer ? this.createTvAccessFeeCartItem(tvAccessFeeOffer, satelliteHardwareSelection) : null;
        if (tvAccessFeeCartItem) {
            videoCartData.monthly.items.push(tvAccessFeeCartItem);
            videoCartData.monthly.totalAmount += tvAccessFeeCartItem.amount;
        }

        videoCartData.monthly.totalAmount += videoCartData.monthly.promotions.totalAmount || 0;

        return videoCartData;
    }


    isCartEmpty = computed(() => {
        return !this._cart() || this.selectedCustomizations.length === 0;
    })

    getTotalTodaysCharges(): number {
        const cart = this.cart();
        const requiredTodaysCharges = Number(cart?.todaysCharges?.totalAmount) || 0;
        return requiredTodaysCharges;
    }

    getSelectedCustomizations(): CartSelectedCustomization[] {
        return this.selectedCustomizations;
    }

    dtvResidentialSatelliteMainGeniesPricing(): number {
        const satelliteHardwareSelection = this.satelliteHardwareSelectionStateService.state();
        const genie2Equipment = satelliteHardwareSelection?.genie2Equipment;
        const tvQty = this.getSatelliteTvQuantity(satelliteHardwareSelection);

        if (genie2Equipment === 'DTV-EQUIPMENT-GENIE2-ADDTL-MINI-GEMINI' && tvQty > 4) {
            const additionalTvQty = tvQty - 4;
            return (this.GEMINI_HARDWARE_FEE * additionalTvQty);
        }

        else if (genie2Equipment === 'DTV-EQUIPMENT-GENIE2-ADDTL-MINI-WRLS' && tvQty > 0) {
            if (tvQty <= 4) {
                return this.WIRELESS_GEMINI_HARDWARE_FEE;
            }

            const additionalTvQty = tvQty - 4;
            return this.WIRELESS_GEMINI_HARDWARE_FEE + (this.GEMINI_HARDWARE_FEE * additionalTvQty);
        }
        else {
            return 0;
        }
    }

    dtvResidentialSatelliteGinieMiniPricing(): number {
        const satelliteHardwareSelection = this.satelliteHardwareSelectionStateService.state();
        const genieOptions = satelliteHardwareSelection.genieOptions;
        const genieMini = genieOptions.find(option => option[this.genieMiniCheckboxKey] === true);

        if (genieMini) {
            const genieMiniQty = this.getNumericValue(this.getSatelliteGenieQuantityValue(genieMini[this.genieMiniQuantityKey]));
            if (genieMiniQty > 3) {
                const additionalMiniQty = genieMiniQty - 3;
                if (additionalMiniQty <= 2) {
                    return this.GEMINI_HARDWARE_FEE * additionalMiniQty;
                }

                const firstTierTotal = this.GEMINI_HARDWARE_FEE * 2;
                const highTierMiniQty = additionalMiniQty - 2;
                return firstTierTotal + (this.WIRELESS_GEMINI_HARDWARE_FEE * highTierMiniQty);
            }
        }

        return 0;
    }

    dtvResidentialSatelliteGinieHdReceiverPricing(): number {
        const satelliteHardwareSelection = this.satelliteHardwareSelectionStateService.state();
        const genieOptions = satelliteHardwareSelection.genieOptions;
        const hdReceiver = genieOptions.find(option => option[this.hdReceiverCheckboxKey] === true);

        if (hdReceiver) {
            const hdReceiverQty = this.getNumericValue(this.getSatelliteGenieQuantityValue(hdReceiver[this.hdReceiverQuantityKey]));
            if (hdReceiverQty > 0) {
                return (this.WIRELESS_GEMINI_HARDWARE_FEE * hdReceiverQty);
            }
        }
        return 0;
    }

    dtvResidentialSatelliteSubHardwarePricing(offer: CartSelectedCustomization): number | undefined {
        const choiceCode = offer.Choice?.ChoiceCode || '';
        if (choiceCode === 'DTV-EQUIPMENT-MAIN-GENIE2') {
            return this.dtvResidentialSatelliteMainGeniesPricing();
        } else if (choiceCode === 'DTV-EQUIPMENT-GENIE-ADDTL-MINI') {
            return this.dtvResidentialSatelliteGinieMiniPricing();
        } else if (choiceCode === 'DTV-EQUIPMENT-GENIE-ADDTL-HD') {
            return this.dtvResidentialSatelliteGinieHdReceiverPricing();
        }

        return undefined;
    }
}
